import { useState } from "react";
import { useGetReferrer } from "../api/settings/useGetReferrer.tsx";
import { Plus } from "lucide-react";

import ReferrerTable from "../components/settings/referrer/referrerTable.tsx";
import ReferrerPagination from "../components/settings/referrer/referrerPagination.tsx";
import ReferrerSearchBar from "../components/settings/referrer/referrerSearchBar.tsx";
import AddReferrerButton from "../components/settings/referrer/referrerAddReferrerButton.tsx";

type Tab = "Referrer";

const TABS: Tab[] = ["Referrer"];

interface ReferrerItem {
  id: number;
  name: string;
  lead_source: string;
  mobile_no: string;
  email: string;
  created_by: string;
  created_at: string;
}

interface ReferrerTabProps {
  data: ReferrerItem[];
  refetch: () => void;
  onSort: (field: string) => void;
  sortField: string;
  sortOrder: "asc" | "desc";
  page: number;
  setPage: (page: number) => void;
  pageSize: number;
  setPageSize: (size: number) => void;
  totalReferrers: number;
  absoluteTotal: number;
  totalPages: number;
  isLoading: boolean;
  onSearch: (searchTerm: string) => void;
}

const ReferrerTab = ({
  data,
  refetch,
  onSort,
  sortField,
  sortOrder,
  page,
  setPage,
  pageSize,
  setPageSize,
  totalReferrers,
  absoluteTotal,
  totalPages,
  isLoading,
  onSearch,
}: ReferrerTabProps) => {
  return (
    <div>
      {/* Top Bar: Add Button + Search Bar */}
      <div className="p-4 border-b border-gray-200 flex flex-row items-center justify-between gap-4">
        <ReferrerSearchBar onSearch={onSearch} />
        <AddReferrerButton
            refetch={refetch}
            trigger={
                <button
                className="bg-[#FEFAD8] text-black font-bold px-3 py-2 md:px-4 rounded-md hover:bg-[#F7E21E] transition-colors min-h-11 md:min-h-0"
                aria-label="Add Referrer"
                >
                <div className="flex flex-row items-center gap-1">
                    <Plus className="w-5 h-5" />
                    <span className="hidden sm:inline">Add Referrer</span>
                </div>
                </button>
            }
            />
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12 text-gray-400 text-sm">
          Loading...
        </div>
      ) : (
        <ReferrerTable
          data={data}
          refetch={refetch}
          onSort={onSort}
          sortField={sortField}
          sortOrder={sortOrder}
        />
      )}

      {/* Pagination */}
      <ReferrerPagination
        page={page}
        setPage={setPage}
        pageSize={pageSize}
        setPageSize={setPageSize}
        totalReferrers={totalReferrers}
        absoluteTotal={absoluteTotal}
        totalPages={totalPages}
      />
    </div>
  );
};

const OthersTab = () => {
  return (
    <div className="p-6">
      <h2 className="text-lg font-bold text-[#1E293B] mb-4">Others Settings</h2>
      <p className="text-gray-400 text-sm">Others settings content goes here.</p>
    </div>
  );
};

const Settings = () => {
  const [activeTab, setActiveTab] = useState<Tab>("Referrer");
  const [sortField, setSortField] = useState<string>("created_at");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [searchName, setSearchName] = useState<string>("");

  const {
    data,
    totalPages,
    totalReferrers,
    absoluteTotal,
    isLoading,
    refetch,
  } = useGetReferrer(page, pageSize, searchName, sortField, sortOrder);

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const handleSearch = (searchTerm: string) => {
    setSearchName(searchTerm);
    setPage(1);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 md:px-6 lg:px-8 py-6">

        {/* Tab Bar */}
        <div className="flex space-x-0 border-b border-gray-200 mb-6">
          {TABS.map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-6 py-3 text-sm font-bold transition-colors focus:outline-none ${
                activeTab === tab
                  ? "border-b-4 border-[#F7E21E] text-[#1E293B]"
                  : "text-gray-400 hover:text-gray-600 border-b-4 border-transparent"
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="bg-white rounded-xl shadow-sm">
          {activeTab === "Referrer" && (
            <ReferrerTab
              data={data}
              refetch={refetch}
              onSort={handleSort}
              sortField={sortField}
              sortOrder={sortOrder}
              page={page}
              setPage={setPage}
              pageSize={pageSize}
              setPageSize={setPageSize}
              totalReferrers={totalReferrers}
              absoluteTotal={absoluteTotal}
              totalPages={totalPages}
              isLoading={isLoading}
              onSearch={handleSearch}
            />
          )}
        </div>

      </div>
    </div>
  );
};

export default Settings;